import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import noop from 'lodash/noop';

import { getForm, getFormLabel, getSubmitButtonLabel } from 'common/entities/form/selectors';
import { getFormSessionStepsEnabled } from 'common/entities/form/sessionRegistration';

import FormNode from './nodes/FormNode';

import FormLogo from './form/FormLogo';
import FormHeadline from './form/FormHeadline';
import FormView from './form/FormView';
import FormErrorContainer from './errors/FormErrorContainer';
import FormSubmitButton from './form/FormSubmitButton';
import FormThemeWrapper from '../containers/FormThemeWrapper';
import { startSubmissionEffect } from '../epics/form';
import { getAttendingNodeValue, getVisibleGroupFormNodes } from '../selectors/form';
import { FormIdContext } from './FormIdContext';
import { FormSessions } from './form/FormSessions';
import { getFormSessions } from '../selectors';
import { shouldShowSessionRegistration } from '../utils/shouldShowSessionRegistration';

const SUBMIT_LABEL = 'Submit';

const FormRenderer = (props) => {
    // Safeguard check to prevent attempting a rendering if the tree hasn't laoded yet.
    if (!props.formIsLoaded) {
        return null;
    }

    return (
        <FormIdContext.Provider value={props.formId}>
            <FormThemeWrapper>
                <FormView isInline={props.isInline} onRequestClose={props.onRequestClose}>
                    {shouldShowSessionRegistration(
                        props.isSessionRegistrationEnabled,
                        props.allowSubmit,
                        props.sessions,
                        props.isNotAttending,
                    ) ? (
                        <FormSessions
                            formNodes={props.formNodes}
                            groupId={props.groupId}
                            formId={props.formId}
                            isPreview={!props.allowSubmit}
                        />
                    ) : (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                props.startSubmissionEffect({
                                    allowSubmit: props.allowSubmit,
                                    formId: props.formId,
                                });
                            }}
                            noValidate
                            aria-atomic
                        >
                            <FormLogo />
                            {!props.headlineDisabled && <FormHeadline label={props.formLabel} />}

                            <div>
                                {props.formNodes.map((node) => (
                                    <FormNode nodeId={node.id} groupId={props.groupId} key={node.id} />
                                ))}
                            </div>

                            {!props.errorContainerDisabled && <FormErrorContainer />}
                            {!props.submitButtonDisabled && (
                                <FormSubmitButton>{props.submitButtonLabel}</FormSubmitButton>
                            )}
                        </form>
                    )}
                </FormView>
            </FormThemeWrapper>
        </FormIdContext.Provider>
    );
};

FormRenderer.propTypes = {
    formIsLoaded: PropTypes.bool.isRequired,
    startSubmissionEffect: PropTypes.func.isRequired,
    formNodes: PropTypes.array.isRequired,
    onRequestClose: PropTypes.func,
    isInline: PropTypes.bool,
    allowSubmit: PropTypes.bool,
    headlineDisabled: PropTypes.bool,
    submitButtonDisabled: PropTypes.bool,
    errorContainerDisabled: PropTypes.bool,
    formId: PropTypes.string,
    groupId: PropTypes.string,
    isSessionRegistrationEnabled: PropTypes.bool,
    submitButtonLabel: PropTypes.string.isRequired,
    formLabel: PropTypes.string,
    sessions: PropTypes.array,
    isNotAttending: PropTypes.bool,
};

FormRenderer.defaultProps = {
    onSubmit: noop,
    onRequestClose: undefined,
    isInline: false,
    allowSubmit: false,
    isMobileRsvp: false,
    headlineDisabled: false,
    submitButtonDisabled: false,
    errorContainerDisabled: false,
    groupId: null,
    formId: null,
    isSessionRegistrationEnabled: false,
    formLabel: undefined,
    sessions: [],
    isNotAttending: false,
};

const mapStateToProps = (state, props) => {
    const { formId, groupId } = props;
    const form = getForm(state, formId);
    const formIsLoaded = Boolean(form && form.root);
    const formNodes = formIsLoaded ? getVisibleGroupFormNodes(state, formId, groupId) : [];
    const isSessionRegistrationEnabled = getFormSessionStepsEnabled(state, formId);
    const submitButtonLabel = getSubmitButtonLabel(state, formId) || SUBMIT_LABEL;
    const formLabel = getFormLabel(state, formId);
    const sessions = getFormSessions(state, formId);
    const isNotAttending = getAttendingNodeValue(state, formId) === '0';

    return {
        formId,
        formNodes,
        formIsLoaded,
        isSessionRegistrationEnabled,
        submitButtonLabel,
        formLabel,
        sessions,
        isNotAttending,
    };
};

const mapDispatchToProps = {
    startSubmissionEffect,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormRenderer);
